/**
 * `forms` — acquisition and contact forms whose backend is the portal itself
 * (`.docs/46-blog-and-forms.md` §6).
 *
 * A client's site posts a visitor's submission to one public door; the portal stores it
 * before it answers, classifies it (`pending`, `clean`, `spam`), meters a clean one and
 * forwards it by mail through Resend. Everything a form's rules depend on is here,
 * because the API validates with these objects and the SPA's form editor resolves with
 * the same ones.
 *
 * **Snake case on every wire** (decision 13), the public door's included: a site reads
 * `receipt_id`, never `receiptId`.
 */
import { z } from 'zod';
import { SLUG_PATTERN } from '../slug.js';
import { DateOnlySchema, Rfc3339Schema } from './primitives.js';
import { WidgetRefSchema } from './refs.js';
/* ----------------------------------------------------------------- addresses */
/**
 * One mailbox, bare: a local part, an `@` and a dotted domain — no display name, no
 * comment, no second address, no whitespace and so no CR or LF.
 *
 * The WHATWG `<input type=email>` rule, with the domain required to have a dot. It is the
 * rule for **every** address a visitor's text can become — a submission's `Reply-To`, an
 * `email` field's value — because an address that reaches a mail header must not be able
 * to carry a second header or a second recipient, and "one plain mailbox" is the shape
 * that can do neither. `security/mime.ts` checks a `Reply-To` against it too.
 */
export const PLAIN_ADDRESS_PATTERN = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
/** RFC 5321's longest usable address, and the `email` field's length. */
export const PLAIN_ADDRESS_MAX = 254;
/** True when the value is exactly one bare mailbox `PLAIN_ADDRESS_PATTERN` admits. */
export function isPlainAddress(value) {
    return value.length <= PLAIN_ADDRESS_MAX && PLAIN_ADDRESS_PATTERN.test(value);
}
const PlainAddressSchema = z
    .string()
    .trim()
    .max(PLAIN_ADDRESS_MAX)
    .refine(isPlainAddress, 'An address is one plain mailbox — name@example.com.');
/* ------------------------------------------------------------------ the fields */
/** Every kind of field a form may declare. */
export const FORM_FIELD_TYPES = [
    'text',
    'email',
    'tel',
    'url',
    'textarea',
    'select',
    'checkbox',
    'number',
    'hidden',
];
export const FormFieldTypeSchema = z
    .enum(FORM_FIELD_TYPES)
    .describe('What the field holds: text (one line), email (one address — the Reply-To candidate), ' +
    'tel, url (http or https), textarea (several lines), select (one of its options), ' +
    'checkbox (true or false), number, or hidden (set by the site, not typed).');
/**
 * The longest value of each type, and so a field's `max_length` when it names none — and
 * the longest it may name. Null where length is not the measure: a select is one of its
 * options, a checkbox a boolean, a number a number.
 *
 * A field may be shorter than its type's default and never longer; the 32 KB body limit
 * is the bound on the whole submission, and this is the bound on one answer in it.
 */
export const FORM_FIELD_MAX_LENGTH = {
    text: 200,
    email: PLAIN_ADDRESS_MAX,
    tel: 40,
    url: 2_000,
    textarea: 5_000,
    select: null,
    checkbox: null,
    number: null,
    hidden: 500,
};
/** A field's own length bound: what it named, or its type's. Null where length is not it. */
export function maxLengthOf(field) {
    return FORM_FIELD_MAX_LENGTH[field.type] === null
        ? null
        : (field.max_length ?? FORM_FIELD_MAX_LENGTH[field.type]);
}
/**
 * A field's name: a lower-case letter, then up to 39 letters, digits and underscores.
 *
 * Starting with a letter is what keeps a field off the control fields (`_hp`,
 * `_elapsed_ms`, `_turnstile`, `_page`), which all start with an underscore: a site can
 * never declare a field the door would read as an instruction.
 */
export const FORM_FIELD_NAME_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
/**
 * Names a field or a honeypot may not have, because every JavaScript object already
 * answers to them: `body.constructor` is `Object` before a visitor sends anything. The
 * door reads a body by own property only (`forms/service.ts` `submitted`); refusing the
 * names keeps every other reader of a form from having to be as careful.
 */
export const FORM_FIELD_NAMES_RESERVED = [
    'constructor',
    'prototype',
    '__proto__',
];
const RESERVED_NAME_MESSAGE = '"constructor", "prototype" and "__proto__" are names every JavaScript object already ' +
    'has, so a value posted under one could not be told from nothing; choose another.';
/** How many fields one form declares, at most. */
export const FORM_FIELDS_MAX = 30;
/** How many options one select offers, at most, and how long each may be. */
export const FORM_SELECT_OPTIONS_MAX = 50;
export const FORM_OPTION_MAX = 100;
/** The longest label a field may carry. */
export const FORM_LABEL_MAX = 100;
export const FormFieldSchema = z
    .strictObject({
    name: z
        .string()
        .regex(FORM_FIELD_NAME_PATTERN, 'A field name is a lower-case letter, then up to 39 letters, digits and underscores.')
        .refine((name) => !FORM_FIELD_NAMES_RESERVED.includes(name), RESERVED_NAME_MESSAGE)
        .describe('The key the site posts the value under — "email", "company_size". A lower-case ' +
        'letter first, then letters, digits and underscores; never starting with "_".'),
    label: z
        .string()
        .trim()
        .min(1)
        .max(FORM_LABEL_MAX)
        .describe('What a person reading the lead sees beside the value — "Work email".'),
    type: FormFieldTypeSchema,
    required: z
        .boolean()
        .default(false)
        .describe('True when a submission without it is refused. A required checkbox must be ticked.'),
    max_length: z
        .int()
        .min(1)
        .optional()
        .describe('The longest value accepted, in characters. Left out, the type’s own: text 200, ' +
        'email 254, tel 40, url 2,000, textarea 5,000, hidden 500. Never longer than that; ' +
        'not for select, checkbox or number.'),
    options: z
        .array(z.string().trim().min(1).max(FORM_OPTION_MAX))
        .min(1)
        .max(FORM_SELECT_OPTIONS_MAX)
        .optional()
        .describe(`A select’s choices, 1 to ${String(FORM_SELECT_OPTIONS_MAX)}, each at most ` +
        `${String(FORM_OPTION_MAX)} characters and named once. Required for a select and ` +
        'refused on every other type.'),
})
    .superRefine((field, ctx) => {
    const ceiling = FORM_FIELD_MAX_LENGTH[field.type];
    if (field.max_length !== undefined) {
        if (ceiling === null) {
            ctx.addIssue({
                code: 'custom',
                path: ['max_length'],
                message: `A ${field.type} field has no length to bound.`,
            });
        }
        else if (field.max_length > ceiling) {
            ctx.addIssue({
                code: 'custom',
                path: ['max_length'],
                message: `A ${field.type} field is at most ${String(ceiling)} characters.`,
            });
        }
    }
    if (field.type === 'select') {
        if (field.options === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'A select needs its options.',
            });
        }
        else if (new Set(field.options).size !== field.options.length) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'Name each option once.',
            });
        }
    }
    else if (field.options !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['options'],
            message: `Only a select has options; this is a ${field.type} field.`,
        });
    }
})
    .describe('One field a form declares: its name, its label, its type and its rules.');
export const FormFieldsSchema = z
    .array(FormFieldSchema)
    .min(1, 'A form declares at least one field.')
    .max(FORM_FIELDS_MAX, `A form declares at most ${String(FORM_FIELDS_MAX)} fields.`)
    .superRefine((fields, ctx) => {
    const seen = new Set();
    fields.forEach((field, index) => {
        if (seen.has(field.name)) {
            ctx.addIssue({
                code: 'custom',
                path: [index, 'name'],
                message: `Two fields are called "${field.name}"; a name is the key a value is posted under.`,
            });
        }
        seen.add(field.name);
    });
})
    .describe(`The fields the form declares, in the order a lead shows them: 1 to ` +
    `${String(FORM_FIELDS_MAX)}, each name used once.`);
/* -------------------------------------------------------------- control fields */
/**
 * What a site may post beside the fields, read by the door and never stored as a field.
 *
 *   - `_hp` — the honeypot, a field a person never sees; filled in, the submission is spam.
 *     A form may rename it (`honeypot_field`), and then that name is the trap and `_hp` is
 *     still ignored.
 *   - `_elapsed_ms` — milliseconds from the form being shown to it being sent; under the
 *     form's `min_fill_seconds`, or missing while that is on, the submission is spam.
 *   - `_turnstile` — the Cloudflare Turnstile token, when the form has a secret.
 *   - `_page` — the page the form was on, kept as the lead's `page_url`.
 */
export const FORM_CONTROL_FIELDS = ['_hp', '_elapsed_ms', '_turnstile', '_page'];
/** The honeypot's name when a form names none. */
export const DEFAULT_HONEYPOT_FIELD = '_hp';
/** A honeypot's name: a field name, with an optional leading underscore. */
export const FORM_HONEYPOT_PATTERN = /^_?[a-z][a-z0-9_]{0,39}$/;
/** Undeclared fields a submission keeps (`extra`): at most this many keys… */
export const FORM_EXTRA_MAX_KEYS = 20;
/** …each value at most this long… */
export const FORM_EXTRA_VALUE_MAX = 2_000;
/** …and each key at most this long. */
export const FORM_EXTRA_KEY_MAX = 100;
/* ------------------------------------------------------------ the form's words */
/** The two words a subject may carry. Nothing a visitor wrote ever reaches a subject. */
export const FORM_SUBJECT_PLACEHOLDERS = ['{form}', '{site}'];
/** The longest subject template. */
export const FORM_SUBJECT_MAX = 120;
/** What a forwarded lead's subject says when the form names none. */
export const DEFAULT_FORM_SUBJECT = 'New submission: {form} on {site}';
/**
 * A subject template: at most 120 characters, no line break, and no `{…}` but the two
 * placeholders — a `{name}` would read like a promise that the visitor's name goes in the
 * subject, and it never does.
 */
export const FormSubjectSchema = z
    .string()
    .trim()
    .min(1)
    .max(FORM_SUBJECT_MAX)
    .refine((value) => !/[\r\n]/.test(value), 'A subject is one line.')
    .refine((value) => (value.match(/\{[^}]*\}/g) ?? []).every((token) => FORM_SUBJECT_PLACEHOLDERS.includes(token)), 'A subject may use {form} and {site} and no other placeholder: nothing a visitor wrote ' +
    'goes in a subject.')
    .describe(`The subject of the mail a lead is forwarded in, at most ${String(FORM_SUBJECT_MAX)} ` +
    'characters. {form} becomes the form’s name and {site} the widget’s site; nothing a ' +
    `visitor wrote is ever put in it. "${DEFAULT_FORM_SUBJECT}" when left out.`);
/** The longest autoresponse body. Plain text, paragraphs split on blank lines. */
export const FORM_AUTORESPONDER_BODY_MAX = 2_000;
/** The longest autoresponse subject. */
export const FORM_AUTORESPONDER_SUBJECT_MAX = 120;
/**
 * How many autoresponses one address gets from one form in a day, at most — one mailbox,
 * however it is spelled (`notifications/addresses.ts` `throttleKeyOf`).
 */
export const AUTORESPONSES_PER_ADDRESS_PER_DAY = 3;
/**
 * How many autoresponses one form sends in a day, at most, to everybody together. The
 * per-address bound stops one inbox being filled; this one stops a form being used to
 * mail many strangers who never wrote — an autoresponse goes to whatever address a
 * visitor typed. Fifty is far beyond a small business's contact form on a good day.
 */
export const AUTORESPONSES_PER_FORM_PER_DAY = 50;
const AutoresponderSubjectSchema = z
    .string()
    .trim()
    .min(1)
    .max(FORM_AUTORESPONDER_SUBJECT_MAX)
    .refine((value) => !/[\r\n]/.test(value), 'A subject is one line.')
    .describe('The subject of the reply a visitor gets — "Thanks, we got your message". Written by ' +
    'the client; nothing the visitor wrote goes in it.');
const AutoresponderBodySchema = z
    .string()
    .trim()
    .min(1)
    .max(FORM_AUTORESPONDER_BODY_MAX)
    .describe(`The reply a visitor gets, plain text, at most ${String(FORM_AUTORESPONDER_BODY_MAX)} ` +
    'characters; a blank line starts a paragraph. It never quotes what the visitor sent — ' +
    'that is what keeps an autoresponder from being a relay for somebody else’s text.');
/** The one key a form may not have: the new-form page's own address segment. */
export const FORM_KEY_RESERVED = 'new';
export const FormKeySchema = z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(SLUG_PATTERN, 'A form key is lower-case words joined by hyphens — "contact", "quote".')
    // `/app/widgets/<ref>/forms/new` is the new-form page, so a form keyed "new" could be
    // made by a tool and then never opened by a person. Refused here, for every door.
    .refine((key) => key !== FORM_KEY_RESERVED, {
    message: `"${FORM_KEY_RESERVED}" is the address of the new-form page; choose another key.`,
})
    .describe('The form’s key on its widget, lower-case words joined by hyphens — "contact", "quote". ' +
    'Unique per widget; how a person and a tool name it. "new" is reserved.');
/** The longest URL a success page or a page_url may be. */
export const FORM_URL_MAX = 2_000;
export const FormSuccessUrlSchema = z
    .string()
    .trim()
    .max(FORM_URL_MAX)
    .pipe(z.url({ protocol: /^https$/, error: 'A success URL is an https:// URL.' }))
    .describe('Where a browser posting without JavaScript is sent after the submission is stored (a ' +
    '303). Only a form with one accepts an application/x-www-form-urlencoded post.');
/**
 * A Turnstile secret key, as Cloudflare issues them (`0x4AAAAAAA…`). Printable, no
 * whitespace — the value goes into a form-encoded body and nowhere else.
 */
export const TurnstileSecretSchema = z
    .string()
    .trim()
    .regex(/^[\x21-\x7e]{10,200}$/, 'That does not look like a Turnstile secret key.')
    .describe('The Cloudflare Turnstile secret key for the site. Write-only: encrypted at rest and ' +
    'never shown again; the form says only whether it has one.');
/* ------------------------------------------------------------------ the states */
export const FORM_STATUSES = ['active', 'closed'];
export const FormStatusSchema = z
    .enum(FORM_STATUSES)
    .describe('active (accepting submissions) or closed (the public door answers 410 Gone; every lead ' +
    'already taken is kept).');
export const FORM_VERDICTS = ['pending', 'clean', 'spam'];
export const FormVerdictSchema = z
    .enum(FORM_VERDICTS)
    .describe('pending (waiting for its Turnstile token to be verified), clean (a lead: metered and ' +
    'forwarded) or spam (kept and shown, never metered, never forwarded).');
/**
 * Why a submission is spam — or, for `turnstile_unverified`, why a clean one is flagged:
 * Cloudflare could not check its token — unreachable on every attempt, or the token
 * expired before the portal could ask — and the portal kept the lead rather
 * than lose it. `marked` is a person's decision.
 */
export const FORM_SPAM_REASONS = [
    'honeypot',
    'too_fast',
    'turnstile',
    'turnstile_unverified',
    'marked',
];
export const FORM_DELIVERY_KINDS = ['notification', 'autoresponse'];
export const FORM_DELIVERY_STATUSES = ['sent', 'failed', 'skipped'];
/** open (nobody has marked it handled) or handled. */
export const FORM_SUBMISSION_STATES = ['open', 'handled'];
/* -------------------------------------------------------------------- the ids */
/** `frm_` and 16 characters of base62 — the only thing a site needs to post a form. */
export const FORM_PUBLIC_ID_PATTERN = /^frm_[A-Za-z0-9]{16}$/;
/** `rcpt_` and 20 characters of base62 — what a visitor's submission is answered with. */
export const RECEIPT_ID_PATTERN = /^rcpt_[A-Za-z0-9]{20}$/;
export const FormPublicIdSchema = z
    .string()
    .regex(FORM_PUBLIC_ID_PATTERN, 'A form’s public id is frm_ and 16 letters and digits.')
    .describe('The form’s public id, frm_…: the address a site posts to. Not a secret.');
export const ReceiptIdSchema = z
    .string()
    .regex(RECEIPT_ID_PATTERN, 'A receipt is rcpt_ and 20 letters and digits.')
    .describe('The submission’s receipt, rcpt_… — what the visitor’s post was answered with.');
/* ------------------------------------------------------------- the public door */
/** The longest `Idempotency-Key` the public door takes. */
export const FORM_IDEMPOTENCY_KEY_MAX = 200;
/**
 * The header a site sends to make a retry safe, and the name its refusal is filed under.
 *
 * Per form. It binds **only** to a submission that was stored and answered 201 — accepted
 * or spam — so a 422, a 429 or any other refusal leaves it free, and a visitor who fixes a
 * field resubmits under the same key. The same key with the same answers is a replay:
 * 200 and the stored receipt. The same key with other answers is `idempotency_key_reused`.
 * "The same answers" is a fingerprint over every declared and undeclared field, the
 * control fields left out, so a retry that recomputes `_elapsed_ms` still matches.
 */
export const FORM_IDEMPOTENCY_HEADER = 'Idempotency-Key';
/** How long the per-IP rate window is, in seconds: ten minutes. */
export const FORM_RATE_WINDOW_SECONDS = 600;
/** What the public door answers a stored submission with — spam included. */
export const FormSubmissionAcceptedSchema = z
    .object({
    accepted: z.literal(true).describe('Always true: the submission is stored.'),
    receipt_id: ReceiptIdSchema,
})
    .describe('The answer to a submission: stored, with its receipt. The same for every stored ' +
    'submission, spam included, so a bot learns nothing from it.');
/**
 * The codes a 422 from the public door carries, one entry per field in `errors[]` —
 * `{ field, code, message }`, with `path` beside `field` and equal to it, which is the
 * portal's own spelling of the same thing:
 *
 *   `required`, `too_long`, `invalid_email`, `invalid_url`, `invalid_tel`,
 *   `invalid_option`, `invalid_number`, `invalid_type` — a declared field's;
 *   `too_many_fields` — more undeclared keys than a submission keeps;
 *   `idempotency_key_reused` — on the field `Idempotency-Key`: a key already bound to a
 *   different submission (`FORM_IDEMPOTENCY_HEADER`).
 */
export const FORM_FIELD_ERROR_CODES = [
    'required',
    'too_long',
    'invalid_email',
    'invalid_url',
    'invalid_tel',
    'invalid_option',
    'invalid_number',
    'invalid_type',
    'too_many_fields',
    'idempotency_key_reused',
];
export const FormFieldErrorSchema = z
    .object({
    field: z.string().describe('The field the error is about, by its name — or "Idempotency-Key".'),
    path: z.string().describe('The same as field: the portal’s own spelling.'),
    code: z.enum(FORM_FIELD_ERROR_CODES).describe('What was wrong, to branch on.'),
    message: z.string().describe('What was wrong, in a sentence a visitor can read.'),
})
    .describe('One field a submission got wrong.');
/* -------------------------------------------------------------------- the views */
export const FormSchema = z
    .object({
    public_id: FormPublicIdSchema,
    key: FormKeySchema,
    widget: WidgetRefSchema,
    name: z.string().describe('The form’s name, as the lead mail and the inbox say it.'),
    status: FormStatusSchema,
    submit_url: z
        .string()
        .describe('The public door a site posts this form to — POST, JSON or a no-script form.'),
    fields: z.array(FormFieldSchema).describe('The fields it declares, in order.'),
    recipients: z.array(z.string()).describe('Who a clean lead is forwarded to.'),
    subject: z.string().describe('The forwarded mail’s subject template.'),
    reply_to_field: z
        .string()
        .nullable()
        .describe('The email field whose value is the forwarded mail’s Reply-To; null → the first.'),
    autoresponder_enabled: z.boolean().describe('True when the visitor gets a reply.'),
    autoresponder_subject: z.string().nullable().describe('The reply’s subject, or null.'),
    autoresponder_body: z.string().nullable().describe('The reply’s text, or null.'),
    honeypot_field: z.string().describe('The trap field’s name; "_hp" unless renamed.'),
    min_fill_seconds: z
        .int()
        .describe('Faster than this from shown to sent is spam; 0 switches the trap off.'),
    rate_limit: z.int().describe('Submissions one IP address may make in ten minutes.'),
    retention_days: z
        .int()
        .nullable()
        .describe('Days a submission is kept before it is deleted; null keeps it for ever.'),
    turnstile_enabled: z
        .boolean()
        .describe('True when the form has a Turnstile secret. The secret itself is never shown.'),
    success_url: z
        .string()
        .nullable()
        .describe('Where a no-script post is sent afterwards; null when the form takes JSON only.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('One form on a forms widget, and everything it does with what it is sent.');
export const FormFieldValueSchema = z
    .union([z.string(), z.number(), z.boolean(), z.null()])
    .describe('The value as stored: text, a number, true or false, or null when left empty.');
export const SubmissionFieldSchema = z
    .object({
    name: z.string().describe('The field’s name.'),
    label: z
        .string()
        .describe('Its label when the lead arrived — a later edit never re-labels it.'),
    type: FormFieldTypeSchema,
    value: FormFieldValueSchema,
})
    .describe('One answer, with the label and type it was given under.');
export const FormDeliverySchema = z
    .object({
    id: z.uuid().describe('The attempt’s id.'),
    kind: z
        .enum(FORM_DELIVERY_KINDS)
        .describe('notification (to the form’s recipients) or autoresponse (to the visitor).'),
    to: z.array(z.string()).describe('Who it was addressed to.'),
    status: z
        .enum(FORM_DELIVERY_STATUSES)
        .describe('sent (Resend took it), failed (it refused, or could not be reached) or skipped ' +
        '(nothing was sent, and error says why).'),
    provider_id: z.string().nullable().describe('Resend’s id for the message, when it took it.'),
    error: z.string().nullable().describe('Why it failed or was skipped; null when sent.'),
    attempt: z.int().describe('Which try this was, from 1, within its round.'),
    round: z.int().describe('1 for the first forwarding; each Resend starts the next.'),
    created_at: Rfc3339Schema,
})
    .describe('One attempt to mail a submission somewhere, and what became of it.');
export const FormSubmissionSchema = z
    .object({
    receipt_id: ReceiptIdSchema,
    form: FormKeySchema,
    form_name: z.string().describe('The form’s name now.'),
    fields: z.array(SubmissionFieldSchema).describe('Every declared field, as it arrived.'),
    extra: z
        .record(z.string(), z.string())
        .describe('Fields the form does not declare, kept rather than lost — usually a site that was ' +
        'deployed before the form was edited.'),
    verdict: FormVerdictSchema,
    spam_reason: z
        .enum(FORM_SPAM_REASONS)
        .nullable()
        .describe('Why it is spam, or turnstile_unverified on a clean one; null otherwise.'),
    reply_to: z.string().nullable().describe('The visitor’s address, when they gave a valid one.'),
    page_url: z.string().nullable().describe('The page the form was on, when the site said.'),
    origin: z.string().nullable().describe('The Origin the browser sent, when it sent one.'),
    read_at: Rfc3339Schema.nullable().describe('When somebody first marked it read.'),
    handled_at: Rfc3339Schema.nullable().describe('When it was marked handled; null while open.'),
    handled_by: z
        .string()
        .nullable()
        .describe('Who marked it handled, by name, when a person did.'),
    received_at: Rfc3339Schema.describe('When the portal stored it.'),
})
    .describe('One submission to a form: the visitor’s answers and what the portal made of them.');
export const FormSubmissionDetailSchema = FormSubmissionSchema.extend({
    deliveries: z
        .array(FormDeliverySchema)
        .describe('Every attempt to mail it, oldest first — the delivery log.'),
}).describe('One submission, with its delivery log.');
/* ------------------------------------------------------------------- the inputs */
const RecipientsSchema = z
    .array(PlainAddressSchema)
    .min(1, 'A form forwards to at least one address.')
    .max(10, 'A form forwards to at most ten addresses.')
    .describe('Who a clean lead is forwarded to, 1 to 10 addresses.');
const ReplyToFieldSchema = z
    .string()
    .regex(FORM_FIELD_NAME_PATTERN, 'Name one of the form’s fields.')
    .describe('The email field whose value becomes the forwarded mail’s Reply-To. Left out or null, ' +
    'the first email field.');
const HoneypotFieldSchema = z
    .string()
    .regex(FORM_HONEYPOT_PATTERN, 'A honeypot is a field name, with an optional leading "_".')
    .refine((name) => !FORM_FIELD_NAMES_RESERVED.includes(name), RESERVED_NAME_MESSAGE)
    .describe(`The trap field a person never sees and a bot fills in. "${DEFAULT_HONEYPOT_FIELD}" ` +
    'when left out.');
const MinFillSecondsSchema = z
    .int()
    .min(0)
    .max(600)
    .describe('A submission sent faster than this after the form was shown is spam (the site sends ' +
    '_elapsed_ms). 0 switches the trap off; 3 when left out.');
const RateLimitSchema = z
    .int()
    .min(1)
    .max(100)
    .describe('How many submissions one IP address may make in ten minutes. 5 when left out.');
const RetentionDaysSchema = z
    .int()
    .min(1)
    .max(3_650)
    .describe('Days a submission is kept, then deleted. 365 when left out; null keeps them for ever.');
/**
 * The fields of a new form. `turnstile_secret` is write-only; everything else comes back
 * in the form's view. The rules that join fields are `formConfigIssues`, applied by
 * `CreateFormSchema` and, on a change, by the service once the change is merged.
 */
export const CreateFormFields = z.strictObject({
    key: FormKeySchema,
    name: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .describe('The form’s name, as the inbox and the forwarded mail say it — "Contact".'),
    fields: FormFieldsSchema,
    recipients: RecipientsSchema,
    subject: FormSubjectSchema.optional(),
    reply_to_field: ReplyToFieldSchema.optional(),
    autoresponder_enabled: z
        .boolean()
        .default(false)
        .describe('True to send the visitor a reply. Off when left out.'),
    autoresponder_subject: AutoresponderSubjectSchema.optional(),
    autoresponder_body: AutoresponderBodySchema.optional(),
    honeypot_field: HoneypotFieldSchema.optional(),
    min_fill_seconds: MinFillSecondsSchema.optional(),
    rate_limit: RateLimitSchema.optional(),
    retention_days: RetentionDaysSchema.nullable().optional(),
    turnstile_secret: TurnstileSecretSchema.optional(),
    success_url: FormSuccessUrlSchema.optional(),
});
/**
 * The rules that join a form's fields, as issues on the field each is about:
 *
 *   - the `reply_to_field` names a declared field of type `email`;
 *   - an autoresponder has a subject and a body, and the form has an email field to
 *     reply to;
 *   - the honeypot is not a declared field and not another control field.
 */
export function formConfigIssues(config) {
    const issues = [];
    const emails = config.fields.filter((field) => field.type === 'email');
    if (config.reply_to_field !== null) {
        const named = config.fields.find((field) => field.name === config.reply_to_field);
        if (named?.type !== 'email') {
            issues.push({
                path: 'reply_to_field',
                message: `The Reply-To field has to be one of the form’s email fields; "${config.reply_to_field}" is not.`,
            });
        }
    }
    if (config.autoresponder_enabled) {
        if (config.autoresponder_subject === null) {
            issues.push({ path: 'autoresponder_subject', message: 'An autoresponder needs a subject.' });
        }
        if (config.autoresponder_body === null) {
            issues.push({ path: 'autoresponder_body', message: 'An autoresponder needs a body.' });
        }
        if (emails.length === 0) {
            issues.push({
                path: 'autoresponder_enabled',
                message: 'An autoresponder replies to an email field, and this form has none.',
            });
        }
    }
    if (config.fields.some((field) => field.name === config.honeypot_field)) {
        issues.push({
            path: 'honeypot_field',
            message: `"${config.honeypot_field}" is one of the form’s fields; the honeypot has to be a field nobody fills in.`,
        });
    }
    if (config.honeypot_field !== DEFAULT_HONEYPOT_FIELD &&
        FORM_CONTROL_FIELDS.includes(config.honeypot_field)) {
        issues.push({
            path: 'honeypot_field',
            message: `"${config.honeypot_field}" is a control field the door already reads for something else.`,
        });
    }
    return issues;
}
export const CreateFormSchema = CreateFormFields.superRefine((input, ctx) => {
    for (const issue of formConfigIssues({
        fields: input.fields,
        reply_to_field: input.reply_to_field ?? null,
        autoresponder_enabled: input.autoresponder_enabled,
        autoresponder_subject: input.autoresponder_subject ?? null,
        autoresponder_body: input.autoresponder_body ?? null,
        honeypot_field: input.honeypot_field ?? DEFAULT_HONEYPOT_FIELD,
    })) {
        ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
});
/**
 * A change to a form. Every field is optional and what is left out is left alone; null
 * clears what may be cleared. `status` closes and reopens it. The joining rules are the
 * service's to check, once the change is merged into the form as it is.
 */
export const UpdateFormFields = z.strictObject({
    name: z.string().trim().min(1).max(100).optional().describe('A new name.'),
    status: FormStatusSchema.optional().describe('closed to stop taking submissions (the door answers 410), active to start again.'),
    fields: FormFieldsSchema.optional().describe('The whole new list of fields. A lead already taken keeps the labels it arrived with.'),
    recipients: RecipientsSchema.optional(),
    subject: FormSubjectSchema.optional(),
    reply_to_field: ReplyToFieldSchema.nullable().optional(),
    autoresponder_enabled: z
        .boolean()
        .optional()
        .describe('True to reply to visitors; false to stop.'),
    autoresponder_subject: AutoresponderSubjectSchema.nullable().optional(),
    autoresponder_body: AutoresponderBodySchema.nullable().optional(),
    honeypot_field: HoneypotFieldSchema.optional(),
    min_fill_seconds: MinFillSecondsSchema.optional(),
    rate_limit: RateLimitSchema.optional(),
    retention_days: RetentionDaysSchema.nullable().optional(),
    turnstile_secret: TurnstileSecretSchema.nullable()
        .optional()
        .describe('A new Turnstile secret key, replacing the old one, or null to switch Turnstile off. ' +
        'Write-only, like the first.'),
    success_url: FormSuccessUrlSchema.nullable().optional(),
});
/** A person's triage of one submission. At least one of the three. */
export const SetFormSubmissionFields = z.strictObject({
    verdict: z
        .enum(['clean', 'spam'])
        .optional()
        .describe('spam to take it out of the inbox; clean ("not spam") to put it back — a submission ' +
        'that was never forwarded is then metered and forwarded.'),
    handled: z.boolean().optional().describe('true to mark it handled, false to reopen it.'),
    read: z
        .literal(true)
        .optional()
        .describe('true to mark it read. Reading never writes by itself.'),
});
/** The filters of an inbox page. */
export const FormSubmissionsQuerySchema = z.strictObject({
    form: FormKeySchema.optional().describe('Only this form’s submissions. Left out, every form’s.'),
    verdict: FormVerdictSchema.optional().describe('Only submissions with this verdict. Left out, pending and clean — the inbox; spam is ' +
        'asked for by name.'),
    state: z
        .enum(FORM_SUBMISSION_STATES)
        .optional()
        .describe('open (not marked handled) or handled. Left out, both.'),
    q: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .optional()
        .describe('Words to find in the answers — a name, an address, a company.'),
});
/** The CSV export's query: one form (its fields are the columns), and optional bounds. */
export const FormSubmissionsCsvQuerySchema = z.strictObject({
    form: FormKeySchema.describe('The form to export; its declared fields are the columns.'),
    verdict: FormVerdictSchema.optional().describe('Only this verdict. Left out, pending and clean.'),
    from: DateOnlySchema.optional().describe('Received on or after this day, UTC.'),
    to: DateOnlySchema.optional().describe('Received on or before this day, UTC.'),
});
//# sourceMappingURL=forms.js.map